import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Code, Shield, EyeOff, Terminal, Zap } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is XSS?",
        icon: "🦠",
        content: (
            <div className="space-y-6">
                <div className="bg-yellow-50 p-6 rounded-2xl border-2 border-yellow-200">
                    <h3 className="font-bold text-xl text-yellow-800 mb-3">Stranger Danger for Browsers</h3>
                    <p className="text-slate-700 mb-4">
                        <strong>Cross-Site Scripting (XSS)</strong> lets attackers inject code into other people's websites.
                    </p>
                    <p className="text-slate-700">
                        Browsers trust code that comes from a website. If you can sneak your code onto a page,
                        the browser will run it, thinking it's safe!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "How It Works",
        icon: "🎭",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-2xl border-2 border-slate-200">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">The Mechanism</h3>
                    <p className="text-slate-700 mb-4">
                        Attackers often use <code>{`<script>`}</code> tags or HTML attributes like <code>onerror</code> to
                        trigger their JavaScript code.
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-slate-300 font-mono text-sm">
                        <div className="text-slate-500">// Evil image tag:</div>
                        <div className="text-blue-600">
                            &lt;img src="x" onerror="<span className="text-red-600">stealCookies()</span>" /&gt;
                        </div>
                    </div>
                </div>
                <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-800 mb-2">⚠️ Why it's bad:</h4>
                    <ul className="text-red-700 text-sm space-y-1">
                        <li>• Steal passwords/cookies</li>
                        <li>• Redirect users to fake sites</li>
                        <li>• Deface websites</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Types of XSS",
        icon: "🗂️",
        content: (
            <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <h3 className="font-bold text-blue-800 mb-2">Reflected</h3>
                        <p className="text-slate-600 text-sm">
                            The attack is in the URL link. You click it, you get hacked instantly.
                        </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <h3 className="font-bold text-purple-800 mb-2">Stored</h3>
                        <p className="text-slate-600 text-sm">
                            The attack is saved in the database (like a comment). EVERYONE who sees it gets hacked!
                        </p>
                    </div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                    <h4 className="font-bold text-yellow-800 mb-2">🎮 Mission: Graffiti Artist</h4>
                    <p className="text-yellow-700 text-sm">
                        Inject a persistent "alert" message into the forum to prove it's vulnerable!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const XssGame = ({ onComplete }: { onComplete: () => void }) => {
    const [userInput, setUserInput] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [isVulnerable, setIsVulnerable] = useState(true);
    const [showAlert, setShowAlert] = useState(false);

    const handleSubmit = () => {
        setSubmitted(true);

        // Check if input contains script-like content
        const lowerInput = userInput.toLowerCase();

        // React blocks <script> tags, but image onerror works.
        const hasXss = lowerInput.includes('onerror=') ||
            lowerInput.includes('javascript:') ||
            (lowerInput.includes('<img') && lowerInput.includes('onerror'));

        if (isVulnerable && hasXss) {
            setShowAlert(true);
            onComplete();
        }
    };

    const sanitize = (input: string) => {
        return input
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;');
    };

    const reset = () => {
        setUserInput('');
        setSubmitted(false);
        setShowAlert(false);
    };

    const injectPayload = (payload: string) => {
        setUserInput(payload);
        setSubmitted(false);
        setShowAlert(false);
    };

    return (
        <div className="text-slate-100">
            {/* Mode Toggle */}
            <div className="flex justify-center mb-8">
                <div className="bg-slate-800 rounded-full p-1 flex">
                    <button
                        onClick={() => { setIsVulnerable(true); reset(); }}
                        className={`px-4 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${isVulnerable ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                        <EyeOff size={14} /> Vulnerable Mode
                    </button>
                    <button
                        onClick={() => { setIsVulnerable(false); reset(); }}
                        className={`px-4 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${!isVulnerable ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                        <Shield size={14} /> Sanitized Mode
                    </button>
                </div>
            </div>

            {/* Playground Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left: Attack Menu */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                        <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                            <Terminal size={18} className="text-purple-400" /> Attack Payloads
                        </h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => injectPayload('<img src=x onerror="alert(\'HACKED\')">')}
                                className="w-full text-left p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors text-xs font-mono text-green-400 border border-slate-600 group"
                            >
                                <div className="font-bold text-slate-300 mb-1 group-hover:text-white">💥 The Alert Bomb</div>
                                &lt;img src=x onerror="alert('HACKED')"&gt;
                            </button>

                            <button
                                onClick={() => injectPayload('<h1 style="color:red; font-size:40px;">I WAS HERE</h1>')}
                                className="w-full text-left p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors text-xs font-mono text-blue-400 border border-slate-600 group"
                            >
                                <div className="font-bold text-slate-300 mb-1 group-hover:text-white">🎨 Style Injection</div>
                                &lt;h1 style="color:red"&gt;...&lt;/h1&gt;
                            </button>

                            <button
                                onClick={() => injectPayload('<marquee scrollamount="20">WEBSITE DEFACED</marquee>')}
                                className="w-full text-left p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors text-xs font-mono text-yellow-400 border border-slate-600 group"
                            >
                                <div className="font-bold text-slate-300 mb-1 group-hover:text-white">🏃 Marquee Tag</div>
                                &lt;marquee&gt;...&lt;/marquee&gt;
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2 italic">
                            Click to load, then press Post!
                        </p>
                    </div>

                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                        <div className="font-bold text-white mb-2">Instructions</div>
                        <ol className="list-decimal list-inside text-sm text-slate-400 space-y-2">
                            <li>Select <strong className="text-red-400">Vulnerable Mode</strong>.</li>
                            <li>Choose an attack payload.</li>
                            <li>Click <strong className="text-blue-400">Post Comment</strong>.</li>
                            <li>Watch the chaos!</li>
                        </ol>
                    </div>
                </div>

                {/* Right: The Forum */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
                        {/* Fake Browser Chrome */}
                        <div className="bg-slate-200 px-4 py-3 flex items-center gap-3">
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                            </div>
                            <div className="flex-1 bg-white rounded-lg px-4 py-1 text-sm text-slate-600 font-mono flex justify-between items-center">
                                <span>https://gorilla-forum.example.org</span>
                                <span className="text-xs text-slate-400">🔒 Secure-ish</span>
                            </div>
                        </div>

                        {/* Fake Page Content */}
                        <div className="p-8 bg-slate-50 min-h-[400px]">
                            <h2 className="text-2xl font-bold text-slate-800 mb-4">🦍 Gorilla Forum</h2>
                            <p className="text-slate-600 mb-6">Leave a comment below:</p>

                            {/* Comment Input */}
                            <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6 shadow-sm">
                                <textarea
                                    value={userInput}
                                    onChange={e => setUserInput(e.target.value)}
                                    placeholder="Type your comment... (try injecting code!)"
                                    className="w-full h-24 p-3 border border-slate-200 rounded-lg text-slate-800 font-mono text-sm resize-none focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                                />
                                <div className="flex gap-3 mt-3">
                                    <button
                                        onClick={handleSubmit}
                                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
                                    >
                                        <Zap size={16} fill="white" /> Post Comment
                                    </button>
                                    <button
                                        onClick={reset}
                                        className="bg-slate-200 hover:bg-slate-300 text-slate-600 font-bold px-6 py-2 rounded-lg transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {/* Rendered Comment Section */}
                            <div className="space-y-4">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recent Comments</div>

                                {/* Default Fake Comment */}
                                <div className="bg-white rounded-xl p-4 border border-slate-200 opacity-60">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs">AJ</div>
                                        <div className="text-sm font-bold text-slate-700">Admin Jane</div>
                                    </div>
                                    <div className="text-slate-600 text-sm">Welcome to the forum! Be nice.</div>
                                </div>

                                {/* User Comment */}
                                {submitted && (
                                    <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-md ring-2 ring-blue-100 animate-fade-in-up">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">G</div>
                                            <div>
                                                <div className="font-bold text-slate-800">You (Guest)</div>
                                                <div className="text-xs text-slate-400">Just now</div>
                                            </div>
                                        </div>
                                        <div className="text-slate-700 bg-slate-50 p-3 rounded-lg overflow-hidden relative">
                                            {isVulnerable ? (
                                                // DANGEROUS: Renders HTML directly (for demo only!)
                                                <div dangerouslySetInnerHTML={{ __html: userInput }} className="danger-zone" />
                                            ) : (
                                                // SAFE: Sanitized output
                                                <div className="font-mono text-sm text-green-700">{sanitize(userInput)}</div>
                                            )}
                                        </div>
                                        {!isVulnerable && (
                                            <div className="mt-2 text-xs text-green-600 font-bold flex items-center gap-1">
                                                <Shield size={12} /> Sanitized: HTML tags were escaped.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Alert Simulation Overlay */}
            {showAlert && (
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowAlert(false)}
                >
                    <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl relative overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="absolute top-0 left-0 w-full h-2 bg-red-500 animate-pulse"></div>
                        <div className="text-6xl mb-4 animate-bounce">💣</div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-2">XSS Attack Successful!</h3>
                        <p className="text-slate-600 mb-6">
                            You injected code successfully.<br />
                            <span className="text-sm text-slate-400">In a real attack, this script could steal your cookies!</span>
                        </p>
                        <button
                            onClick={() => setShowAlert(false)}
                            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-3 rounded-xl transition-colors shadow-lg shadow-red-200"
                        >
                            I Understand
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const XssSandbox = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('xss', 200);
    };

    return (
        <LessonWrapper
            moduleName="XSS Playground"
            moduleEmoji="🎨"
            moduleColor="yellow"
            lessons={LESSONS}
            completed={completed}
            activityLabel="SandBox"
        >
            <XssGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default XssSandbox;
